import { supabase } from '@/integrations/supabase/client'
import { Sentry } from '@/lib/sentry'

/**
 * Invoke a Supabase edge function with automatic error extraction and Sentry reporting.
 *
 * Drop-in replacement for `supabase.functions.invoke()` that:
 * 1. Extracts the real server error message (instead of generic "non-2xx status code")
 * 2. Reports failures to Sentry with the function name and request body
 * 3. Returns the same `{ data, error }` shape for easy migration
 */
export async function invokeEdgeFunction<T = unknown>(
  functionName: string,
  options?: { body?: Record<string, unknown> },
): Promise<{ data: T | null; error: { message: string; status?: number } | null }> {
  const { data, error } = await supabase.functions.invoke(functionName, options)

  if (error) {
    // Extract the real error and HTTP status from the response
    const { message: serverMessage, status } = await extractErrorDetails(error)

    // Only report unexpected errors (5xx / network) to Sentry.
    // 4xx errors are expected validation failures (bad input, auth, etc.)
    if (!status || status >= 500) {
      Sentry.captureException(new Error(serverMessage), {
        tags: { edgeFunction: functionName },
        extra: { functionName, status, body: options?.body },
      })
    }

    return { data: null, error: { message: serverMessage, ...(status != null && { status }) } }
  }

  return { data: data as T, error: null }
}

/**
 * Read the actual error message from a Supabase FunctionsHttpError.
 *
 * The Supabase JS client wraps non-2xx responses in a generic
 * "Edge Function returned a non-2xx status code" error and hides
 * the real JSON body. This reads it from `error.context` (a Response).
 */
async function extractErrorDetails(fnError: { message: string; context?: Response }): Promise<{ message: string; status: number | null }> {
  const status = fnError.context?.status ?? null

  if (fnError.context && typeof fnError.context.json === 'function') {
    try {
      const body = await fnError.context.json()
      if (body?.error && typeof body.error === 'string') {
        return { message: body.error, status }
      }
    } catch {
      // Body already consumed or not JSON
    }
  }
  return { message: fnError.message || 'An unexpected error occurred', status }
}
